import { PageLoading } from "@ant-design/pro-components";
import { App } from "antd";
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useMemo,
  useReducer,
} from "react";
import auth from "services/admin/auth";
import reference from "services/guest/reference";
import useRequest from "utils/useRequest";
import { Action, DataType, ReducerType, References, Type } from "./types";
import { AuthContext, AuthContextType } from "./auth";
import { SettingsContext, SettingsContextType } from "./settings";
import { Lang } from "services/guest/reference/types";
import { LangContext, LangContextType } from "./lang";
import { LocaleEnum } from "types";

const DEFAULT_REFERENCE: References = {
  loading: true,
};

const DEFAULT_DATA: DataType = {
  authorized: false,
  settings: {
    layout: "side",
    navTheme: "light",
    colorWeak: false,
    title: "TVMS",
    fixedHeader: true,
    fixSiderbar: true,
    contentWidth: "Fluid",
    listen_socket: false,
  },
  references: DEFAULT_REFERENCE,
  langs: [],
};

const reducer: ReducerType = (state, action) => {
  switch (action[0]) {
    case Action.SET_SETTINGS:
      return { ...state, settings: action[1] };
    case Action.SIGN_IN:
      return { ...state, auth: action[1], authorized: true };
    case Action.SET_REFERENCES:
      return { ...state, references: action[1] };
    case Action.SET_LANGS:
      return { ...state, langs: action[1] };
    case Action.SIGN_OUT:
      auth.remToken();
      return { ...state, auth: undefined, authorized: false };
    default:
      return state;
  }
};

const StoreContext = createContext<Type>([
  DEFAULT_DATA,
  () => undefined,
  {
    info: () => undefined,
  },
]);

export const StoreProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { message } = App.useApp();
  const [state, dispatch] = useReducer(reducer, DEFAULT_DATA);

  const { loading: authLoading, refresh: refreshAuth } = useRequest(auth.info, {
    onSuccess: (data) => dispatch([Action.SIGN_IN, data]),
    ready: auth.hasToken(),
  });

  const { loading: langLoading } = useRequest(reference.listLang, {
    onSuccess: (data) => dispatch([Action.SET_LANGS, data || []]),
    onError: (error) => message.error(error.message),
  });

  const isLoading = authLoading || langLoading;
 
  const authValue = useMemo<AuthContextType>(
    () => [state.auth, dispatch],
    [state.auth],
  );
  const settingsValue = useMemo<SettingsContextType>(
    () => [state.settings, dispatch],
    [state.settings],
  );

  const langValue = useMemo<LangContextType> (
      () => [state.langs, dispatch],
    [state.langs],
  );


  if (isLoading) {
    return <PageLoading />;
  }

  return (
    <StoreContext.Provider value={[state, dispatch, { info: refreshAuth }]}>
      <AuthContext.Provider value={authValue}>
        <SettingsContext.Provider value={settingsValue}>
          <LangContext.Provider value={langValue}>
              {children}
          </LangContext.Provider>
        </SettingsContext.Provider>
      </AuthContext.Provider>
    </StoreContext.Provider>
  );
};

export const useStore = (): Type => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error("useStore must be used within StoreProvider");
  }
  return context;
};
